// Même logique que dans api.js : en production tout est servi par le même
// serveur, donc les chemins sont relatifs. Sans cela, l'adresse « localhost »
// du fichier .env serait figée dans le build et les documents deviendraient
// inaccessibles pour les autres utilisateurs.
const API_URL =
  process.env.NODE_ENV === 'production'
    ? '/api'
    : process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const FILES_BASE = API_URL.replace(/\/api\/?$/, '');

export function fileUrl(storedName) {
  return `${FILES_BASE}/uploads/${storedName}`;
}

export function formatFileSize(bytes) {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

export function projectCode(id) {
  return `ZGP-${String(id).padStart(3, '0')}`;
}

export function taskCode(id) {
  return `TSK-${String(id).padStart(4, '0')}`;
}

export function initials(name) {
  if (!name) return '?';
  return name.substring(0, 2).toUpperCase();
}

export function userDisplayName(user) {
  if (!user) return 'Inconnu';
  return user.username;
}

export function formatDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

// Les <input type="date"> natifs affichent mm/dd/yyyy ou jj/mm/aaaa selon la
// langue configurée sur la machine de l'utilisateur (pas selon la langue de
// la page) — ces deux fonctions permettent d'utiliser un champ texte qui
// affiche toujours jj/mm/aaaa, quel que soit le PC.
export function isoToDisplayDate(iso) {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '';
  return `${m[3]}/${m[2]}/${m[1]}`;
}

export function displayDateToIso(display) {
  if (!display) return '';
  const m = String(display).trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null; // saisie incomplète ou invalide
  const day = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  const year = parseInt(m[3], 10);
  // On vérifie que la date existe réellement : sans ce contrôle, une saisie
  // comme 15/15/2026 produisait "2026-15-15" — une date impossible qui
  // cassait ensuite tout le calcul d'échelle du diagramme de Gantt.
  if (month < 1 || month > 12) return null;
  const daysInMonth = new Date(year, month, 0).getDate();
  if (day < 1 || day > daysInMonth) return null;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

// Renvoie un objet Date valide, ou null si la valeur est absente ou corrompue.
// Permet aux vues de survivre à une donnée invalide au lieu de planter.
export function toValidDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

export function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "à l'instant";
  if (mins < 60) return `il y a ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days} j`;
}

export const STATUS_LABELS = {
  Todo: 'À faire',
  In_Progress: 'En cours',
  In_Review: 'Revue',
  Done: 'Terminé',
  Blocked: 'Bloqué',
};

export const STATUS_BADGE_CLASS = {
  Todo: 'badge-todo',
  In_Progress: 'badge-active',
  In_Review: 'badge-review',
  Done: 'badge-completed',
  Blocked: 'badge-blocked',
};

export const PRIORITY_BADGE_CLASS = {
  Urgent: 'badge-priority-high',
  High: 'badge-priority-high',
  Medium: 'badge-priority-medium',
  Low: 'badge-priority-low',
};

export const PROJECT_STATUS_BADGE_CLASS = {
  Active: 'badge-active',
  Delayed: 'badge-delayed',
  Completed: 'badge-completed',
  Cancelled: 'badge-blocked',
};

export const PROJECT_STATUS_LABELS = {
  Active: 'Actif',
  Delayed: 'En retard',
  Completed: 'Terminé',
  Cancelled: 'Annulé',
};

// Rôles autorisés à annuler un projet
export const CAN_CANCEL_PROJECT_ROLES = ['Director', 'Project_Lead'];

// Rôles autorisés à réinitialiser le mot de passe d'un autre utilisateur.
// Doit rester aligné avec ROLES_REINITIALISATION dans backend/server_mock.js :
// le contrôle côté serveur fait foi, celui-ci ne fait que masquer le bouton.
export const CAN_RESET_PASSWORD_ROLES = ['Director', 'Project_Lead'];

export const PROJECT_PRIORITIES = ['P1', 'P2', 'P3', 'P4'];

export const PROJECT_PRIORITY_LABELS = {
  P1: 'P1 - Critique',
  P2: 'P2 - Haute',
  P3: 'P3 - Moyenne',
  P4: 'P4 - Basse',
};

export const PROJECT_PRIORITY_BADGE_CLASS = {
  P1: 'badge-priority-high',
  P2: 'badge-priority-high',
  P3: 'badge-priority-medium',
  P4: 'badge-priority-low',
};

// --- Demandes d'achat --------------------------------------------------------
// Les numéros de requête (ZMSM-R-…) étaient auparavant saisis dans la
// description des tâches ; ils ont désormais un champ dédié, ce qui les rend
// filtrables.

export const PURCHASE_STATUSES = ['a_lancer', 'lancee', 'commandee', 'livree'];

export const PURCHASE_STATUS_LABELS = {
  a_lancer: 'À lancer',
  lancee: 'Requête lancée',
  commandee: 'Commandée',
  livree: 'Livrée',
};

export const PURCHASE_STATUS_BADGE = {
  a_lancer: 'badge-todo',
  lancee: 'badge-active',
  commandee: 'badge-review',
  livree: 'badge-completed',
};

// --- Échéances ---------------------------------------------------------------

/** Une tâche est en retard si son échéance est passée et qu'elle n'est pas terminée. */
export function isOverdue(task) {
  if (!task || !task.due_date || task.status === 'Done') return false;
  return String(task.due_date).slice(0, 10) < new Date().toISOString().slice(0, 10);
}

/** Nombre de jours de retard (0 si la tâche n'est pas en retard). */
export function joursDeRetard(task) {
  if (!isOverdue(task)) return 0;
  const echeance = new Date(String(task.due_date).slice(0, 10));
  const jour = new Date(new Date().toISOString().slice(0, 10));
  return Math.round((jour - echeance) / 86400000);
}

// Rôles autorisés à archiver / restaurer un projet.
// Doit rester aligné avec ROLES_ARCHIVAGE dans backend/server_mock.js.
export const CAN_ARCHIVE_PROJECT_ROLES = ['Director', 'Project_Lead'];
